import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format, addDays, subDays, isToday, startOfDay, isSameDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { useGameCounts } from "@/hooks/useGameCounts";

interface DateNavigatorProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  className?: string;
  leagueId?: number;
}

export function DateNavigator({
  selectedDate,
  onDateChange,
  className,
  leagueId
}: DateNavigatorProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const today = startOfDay(new Date());
  const startDate = subDays(today, 6); // 6 days ago
  
  const { getCountForDate, hasLiveGamesForDate } = useGameCounts({ 
    startDate: startDate, 
    days: 14, // 6 past + today + 7 future
    leagueId 
  });

  // Generate array of 14 days (6 past + today + 7 future)
  const generateDateChips = () => {
    const chips = [];
    for (let i = -6; i <= 7; i++) {
      const date = addDays(today, i);
      const gameCount = getCountForDate(date);
      const hasLive = hasLiveGamesForDate(date);
      const isTodayDate = isToday(date);
      const isSelected = isSameDay(date, selectedDate);
      
      chips.push({
        date,
        weekday: format(date, 'EEE', { locale: enUS }).toUpperCase(),
        dateLabel: format(date, 'dd.MM.'),
        isToday: isTodayDate,
        isSelected,
        isPast: date < today && !isTodayDate,
        gameCount,
        hasLive
      });
    }
    return chips;
  };

  const dateChips = generateDateChips();

  // Auto-scroll to center "TODAY" on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const todayIndex = dateChips.findIndex(chip => chip.isToday);
      if (todayIndex !== -1) {
        const chipWidth = 64; // min-w-[64px]
        const gap = 4; // gap-1
        const scrollPosition = (chipWidth + gap) * todayIndex - (scrollContainerRef.current.offsetWidth / 2) + (chipWidth / 2);
        scrollContainerRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }
  }, []);

  const handleDateClick = (date: Date) => {
    onDateChange(startOfDay(date));
  };

  return (
    <div className={cn("relative", className)}>
      {/* Flashscore-style Date Carousel */}
      <div 
        ref={scrollContainerRef}
        className="flex gap-1 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {dateChips.map((chip, index) => (
          <button
            key={index}
            onClick={() => handleDateClick(chip.date)}
            aria-label={`${chip.weekday} ${chip.dateLabel}`}
            aria-current={chip.isToday ? "date" : undefined}
            className={cn(
              "flex flex-col items-center justify-center min-w-[64px] h-[56px] px-2 py-1.5 rounded-md border transition-all snap-start touch-manipulation",
              chip.isSelected 
                ? "bg-primary text-primary-foreground border-primary font-bold shadow-sm" 
                : "border-gray-light text-gray-medium hover:bg-gray-50 hover:border-gray-medium",
              chip.isToday && !chip.isSelected && "border-primary/50 text-primary font-semibold",
              chip.isPast && !chip.isSelected && "opacity-50",
              chip.hasLive && !chip.isSelected && "border-red-500/30"
            )}
          >
            {/* Weekday or HOJE */}
            <div className={cn(
              "text-[10px] font-bold uppercase tracking-wide mb-0.5",
              chip.isSelected && "text-primary-foreground"
            )}>
              {chip.isToday ? 'TODAY' : chip.weekday}
            </div>
            
            {/* Date */}
            <div className={cn(
              "text-sm font-bold",
              chip.isSelected && "text-primary-foreground"
            )}>
              {chip.dateLabel}
            </div>
            
            {/* Game Count Badge */}
            {chip.gameCount > 0 && (
              <Badge 
                variant={chip.isSelected ? "secondary" : "outline"}
                className={cn(
                  "mt-0.5 h-4 px-1 text-[10px] font-bold",
                  chip.isSelected && "bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30",
                  chip.hasLive && !chip.isSelected && "bg-red-50 text-red-600 border-red-200"
                )}
              >
                {chip.gameCount}
              </Badge>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}